import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { EyeIcon } from "@heroicons/react/24/outline";
import { getStatusBadgeClass, truncateText } from "../utils/Utils";
import Loader from "../components/Loader";
import { AuthContext } from "../context/AuthContext";
import DownloadModFileButton from "../components/DownloadModFileButton";
import { IoArrowBackSharp } from "react-icons/io5";

const MyFiles = () => {
  const { user } = useContext(AuthContext);
  const [files, setFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await axiosInstance.get(`/users/${user.id}/files`);
        setFiles(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const filteredFiles = files.filter(
    (file) =>
      file.file_id
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      file.file_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Loader />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 dark:bg-gray-900 dark:text-white">
      <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 flex items-center">
        {/* Go Back Button */}
        <button
          onClick={() => window.history.back()} // This will navigate back to the previous page
          className="rounded-full hover:bg-gray-200 transition-all mr-2"
        >
          <IoArrowBackSharp size={24} />
        </button>{" "}
        All Files
      </h3>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          className="border border-gray-300 p-2 w-full rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          placeholder="Search by file id, file name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Table for displaying files */}
      <table className="min-w-full table-auto bg-white border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-700">
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              File ID
            </th>
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              Vehicle
            </th>
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              File Name
            </th>
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              Credit
            </th>
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              Price Status
            </th>
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              Status
            </th>
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              Feasible
            </th>
            <th className="px-4 py-2 border-b text-left dark:border-gray-600">
              Mod File
            </th>
            <th className="px-4 py-2 border-b text-center align-middle dark:border-gray-600">
              View
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredFiles.map((file) => (
            <tr
              key={file.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <td className="px-4 py-2 border-b dark:border-gray-600">
                {file.file_id}
              </td>
              <td className="px-4 py-2 border-b dark:border-gray-600">
                {file.brand}
              </td>
              <td className="px-4 py-2 border-b dark:border-gray-600">
                {truncateText(file.file_name, 23)}
              </td>
              <td className="px-4 py-2 border-b dark:border-gray-600">
                {file.price}
              </td>
              <td className="px-4 py-2 border-b dark:border-gray-600">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                    file.price_status
                  )}`}
                >
                  {file.price_status}
                </span>
              </td>
              <td className="px-4 py-2 border-b dark:border-gray-600">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                    file.status
                  )}`}
                >
                  {truncateText(file.status, 10).replace(/_/g, " ")}
                </span>
              </td>
              <td className="px-4 py-2 border-b">
                {file.feasible && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                      file.feasible
                    )}`}
                  >
                    {file.feasible}
                  </span>
                )}
              </td>
              <td className="px-4 py-2 border-b text-center align-middle dark:border-gray-600">
                <DownloadModFileButton
                  fileId={file.id}
                  fileName={file.mod_file_name}
                  status={file.status}
                  price_status={file.price_status}
                  isTable={true}
                />
              </td>
              <td className="px-4 py-2 border-b text-center align-middle dark:border-gray-600">
                <button className="text-blue-500 dark:text-blue-400 py-1 rounded">
                  <Link to={`/view-file/${file.id}`}>
                    <EyeIcon className="h-5 w-5 mr-1" />
                  </Link>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MyFiles;
